import { OverlayLoader } from 'components/Loaders'
import Modal from 'components/Modal'
import { COOKIE_CONFIG } from 'constants/cookies'
import { actions, selectors } from 'data'
import { UserAddress, UserType } from 'data/types'
import Cookies from 'js-cookie'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import WelcomeModal from 'scenes/App/OnBootMessages/WelcomeModal'
import { updateAddress, updateUserPersonalInfo } from 'services/api/user'

import Address from './Address'
import GoldPrompt from './GoldPrompt'
import PersonalInfo from './PersonalInfo'
import Resubmission from './Resubmission'

const mapAddress = (apiAddress?: UserAddress) => ({
  city: apiAddress?.city ?? '',
  country: apiAddress?.country ?? 'UK',
  firstLine: apiAddress?.line1 ?? '',
  postcode: apiAddress?.postCode ?? '',
  secondLine: apiAddress?.line2 ?? '',
  state: apiAddress?.state ?? ''
})

const getCurrentStep = (
  user: UserType | undefined,
  seenResubmission: boolean,
  seenWelcome: boolean
): KYCFormStep => {
  if (user?.resubmission && !seenResubmission) {
    return 'Resubmission'
  }
  if (!seenWelcome) {
    return 'Welcome'
  }
  if (!user || !user.firstName || !user.lastName || !user.dob) {
    return 'PersonalInfo'
  }
  if (!user.address || !user.address.line1) {
    return 'Address'
  }

  return 'GoldPrompt'
}

type KYCFormStep = 'Resubmission' | 'Welcome' | 'PersonalInfo' | 'Address' | 'GoldPrompt'
type Props = {
  onDone: () => void
  user: UserType
}
const Steps = ({ onDone, user }: Props) => {
  const [loading, setLoading] = useState(false)
  const [seenResubmission, setSeenResubmission] = useState(false)
  const [seenWelcome, setSeenWelcome] = useState(Cookies.get('WelcomeDismissed'))
  const dispatch = useDispatch()
  const config = useSelector(selectors.config.getConfig)
  const step = getCurrentStep(user, seenResubmission, seenWelcome)

  // Remove this once silverRemoval is enabled in all envs
  useEffect(() => {
    if (step === 'GoldPrompt' && config.silverRemoval) {
      onDone()
    }
  }, [step])

  const onError = (res) =>
    dispatch(
      actions.app.addNotif({
        created: +new Date(),
        description: res.error.cause,
        duration: 5,
        type: 'danger'
      })
    )

  const onPersonalInfoSubmit = async (firstName, lastName, dob) => {
    setLoading(true)
    const res = await updateUserPersonalInfo({ dob, firstName, lastName })
    setLoading(false)

    if (!res.ok) {
      onError(res)
      return
    }

    dispatch(actions.auth.getUser())
  }

  const onAddressSubmit = async (address) => {
    setLoading(true)
    const res = await updateAddress(address)
    dispatch(actions.analytics.trackEvent({ key: 'Address Information Entered', properties: {} }))
    setLoading(false)

    if (!res.ok) {
      onError(res)
      return
    }

    dispatch(actions.auth.getUser())

    if (config.silverRemoval) {
      onDone()
      dispatch(actions.app.showModal('TIER_TWO'))
    }
  }

  if (!user) return <OverlayLoader />

  switch (step) {
    case 'Resubmission':
      return <Resubmission onClose={() => setSeenResubmission(true)} />

    case 'Welcome':
      return (
        <WelcomeModal
          onClose={() => {
            Cookies.set('WelcomeDismissed', 'true', COOKIE_CONFIG)
            setSeenWelcome(true)
          }}
          kycNeeded
        />
      )

    case 'PersonalInfo':
      return (
        <PersonalInfo
          firstname={user?.firstName}
          lastname={user?.lastName}
          dob={user?.dob}
          loading={loading}
          onSubmit={onPersonalInfoSubmit}
        />
      )

    case 'Address':
      return (
        <Address
          address={mapAddress(user.address)}
          loading={loading}
          onSubmit={onAddressSubmit}
          user={user}
        />
      )

    // remove all 'GoldPrompt' references once config.silverRemoval is true in prod
    case 'GoldPrompt':
      return (
        <GoldPrompt
          onDepositClick={() => {
            dispatch(actions.app.showModal('DEPOSIT'))
            onDone()
          }}
          onGoldClick={() => {
            dispatch(actions.app.showModal('TIER_TWO'))
            onDone()
          }}
        />
      )

    default:
      return null
  }

  return null
}

const StepsModal = (props: Props) => (
  <Modal>
    <Steps {...props} />
  </Modal>
)

export default StepsModal
